import { useState, useEffect } from 'react';
import axios from 'axios';
import browserHistory from '../../history';
import Header from './Header';

const REQUIRED_FIELDS = ['firstname', 'lastname', 'gender', 'country', 'state', 'postalCode'];

function DialingSelect({ codeVal, codeOnChange, numVal, numOnChange, disabled, numPlaceholder, fieldStyle, selectStyle, optStyle, inputStyle, options }) {
  return (
    <div style={{ display: 'flex', gap: '8px' }}>
      <div style={{ ...fieldStyle, width: '130px', flexShrink: 0, marginBottom: 0 }}>
        <select value={codeVal} onChange={codeOnChange} disabled={disabled} style={selectStyle}>
          <option style={optStyle} value="">Code</option>
          {(options || []).map((o, i) => <option key={i} style={optStyle} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      <div style={{ ...fieldStyle, flex: 1, marginBottom: 0 }}>
        <input type="tel" value={numVal} onChange={numOnChange} placeholder={numPlaceholder} disabled={disabled} style={inputStyle} />
      </div>
    </div>
  );
}

export function isProfileComplete(user) {
  return REQUIRED_FIELDS.every(f => !!(user[f] && user[f].toString().trim()));
}

export default function Profile() {
  const appStorage = window.app?.storage || localStorage;
  const token = appStorage.getItem('token') || localStorage.getItem('token') || sessionStorage.getItem('token');
  const isAuthenticated = !!token;
  const appname = window.app?.appName;
  const environmentVariable = window.app?.appEnvName;
  const submissionLambdaUrl = window.site?.submissionApiBaseUrl;
  const commonApi = (window.site?.config || {}).commonApi;
  const authUserId = appStorage.getItem('user_id') || localStorage.getItem('userid') || sessionStorage.getItem('userid');

  const [isLoading, setIsLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [documentId, setDocumentId] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [subscription, setSubscription] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const [userDetails, setUserDetails] = useState({
    firstname: '', lastname: '', companyName: '', email: '',
    address: '', city: '', country: '', state: '', postalCode: '',
    homePhoneDialingCode: '', homePhone: '',
    mobileDialingCode: '', mobileNumber: '',
    ethnicity: '', gender: '',
  });

  const showToast = (message, type = 'error') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
  };

  const fetchRegions = (alpha3, setter) => {
    axios.post(commonApi + '/getregions?&alpha3=' + alpha3)
      .then(r => { if (r?.data?.result) setter(r.data.result); })
      .catch(() => {});
  };

  const getDialingCodeOptions = () =>
    countries.map(c => ({ value: c.idc || `+${c.phone_code}`, label: `${c.countryname} (${c.idc || '+' + c.phone_code})` }));

  useEffect(() => {
    axios.post(commonApi + '/getcountries?&appname=' + (appname || 'envoidemo'))
      .then(r => { if (r?.data?.result) setCountries(r.data.result); })
      .catch(() => {});

    const userId = localStorage.getItem('uid') || authUserId;
    if (token && userId) fetchUserData(userId);
  }, []);

  const fetchUserData = async (userId) => {
    try {
      setIsLoading(true);
      const resp = await axios.post(
        `${submissionLambdaUrl}/user?appname=${appname || 'envoidemo'}&token=${token}&env=${environmentVariable || 'dev'}&id=${userId}`,
        {},
        { headers: { 'accept': 'application/json, text/plain, */*', 'content-length': '0' } }
      );
      if (resp.data?.result?.data?.[0]) {
        const u = resp.data.result.data[0];
        setDocumentId(u._id || userId);
        const newDetails = {
          firstname: u.firstname || '',
          lastname: u.lastname || '',
          companyName: u.companyAddress?.name || '',
          email: u.emailid || u.email || '',
          address: u.companyAddress?.address || u.address || '',
          city: u.companyAddress?.city || u.city || '',
          country: u.companyAddress?.country || u.country || '',
          state: u.companyAddress?.state || u.state || '',
          postalCode: u.companyAddress?.postalCode || u.zipcode || '',
          homePhoneDialingCode: u.homePhone?.dialingCode || '',
          homePhone: u.homePhone?.number || '',
          mobileDialingCode: u.mobileNumber?.dialingCode || '',
          mobileNumber: u.mobileNumber?.number || '',
          ethnicity: u.ethnicity || '',
          gender: u.gender || '',
        };
        setUserDetails(newDetails);
        setIsEditing(!isProfileComplete(newDetails));
        if (newDetails.country) fetchRegions(newDetails.country, setRegions);
        setSubscription({
          subscriptionend:    u.subscriptionend    || '',
          subscriptionid:     u.subscriptionid     || '',
          subscriptionplanid: u.subscriptionplanid || '',
          subscriptionstart:  u.subscriptionstart  || '',
          subscriptiontype:   u.subscriptiontype   || '',
        });
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field, value) => {
    if (field === 'country') {
      setUserDetails(prev => ({ ...prev, country: value, state: '' }));
      setRegions([]);
      if (value) fetchRegions(value, setRegions);
    } else {
      setUserDetails(prev => ({ ...prev, [field]: value }));
    }
  };

  const saveProfile = async () => {
    if (!userDetails.firstname?.trim()) { showToast('First Name is required.'); return; }
    if (!userDetails.lastname?.trim()) { showToast('Last Name is required.'); return; }
    if (!userDetails.gender) { showToast('Gender is required.'); return; }
    if (!userDetails.country) { showToast('Country is required.'); return; }
    if (!userDetails.state) { showToast('State / Region is required.'); return; }
    if (!userDetails.postalCode?.trim()) { showToast('Postal Code is required.'); return; }
    try {
      setIsLoading(true);
      const _id = documentId || authUserId;
      const resp = await axios.post(
        `${submissionLambdaUrl}/manageUser?appname=${appname || 'envoidemo'}&env=${environmentVariable || 'dev'}&token=${token}&_id=${_id}`,
        {
          firstname: userDetails.firstname,
          lastname: userDetails.lastname,
          emailid: userDetails.email,
          companyAddress: {
            name: userDetails.companyName,
            address: userDetails.address,
            city: userDetails.city,
            country: userDetails.country,
            state: userDetails.state,
            postalCode: userDetails.postalCode,
          },
          homePhone: userDetails.homePhone ? { dialingCode: userDetails.homePhoneDialingCode, number: userDetails.homePhone } : undefined,
          mobileNumber: userDetails.mobileNumber ? { dialingCode: userDetails.mobileDialingCode, number: userDetails.mobileNumber } : undefined,
          ethnicity: userDetails.ethnicity,
          gender: userDetails.gender,
        },
        { headers: { 'accept': 'application/json, text/plain, */*', 'content-type': 'application/json' } }
      );
      showToast('Profile saved successfully.', 'success');
      setIsEditing(false);

      // Check subscription — if free/not subscribed redirect to subscription plans
      const updatedUser = resp.data?.result?.data?.[0] || resp.data?.result || {};
      const subscriptionId = updatedUser.subscriptionId || updatedUser.planId || updatedUser.subscription?.planId || '';
      const isFreeOrUnsubscribed = !subscriptionId || subscriptionId === 'free';
      if (isFreeOrUnsubscribed) {
        setTimeout(() => {
          browserHistory.push(`/pricing?appname=${appname}`);
        }, 1200);
      }
    } catch (err) {
      console.error('Error saving profile:', err);
      showToast('Failed to save. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const S = {
    page:         { backgroundColor: 'var(--bgColor)', minHeight: '100vh', paddingTop: '53px' },
    container:    { maxWidth: '960px', margin: '0 auto', padding: '32px 20px' },
    title:        { color: '#ffffff', fontSize: '22px', fontWeight: 700, marginBottom: '24px' },
    sectionTitle: { color: '#ffffff', fontSize: '16px', fontWeight: 600, margin: '0 0 20px' },
    label:        { display: 'block', color: '#a0aec0', fontSize: '13px', marginBottom: '6px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' },
    input:        { width: '100%', background: 'transparent', border: 'none', outline: 'none', color: '#ffffff', fontSize: '15px', padding: 0, height: 'auto' },
    select:       { width: '100%', background: '#1e2330', border: 'none', outline: 'none', color: '#ffffff', fontSize: '15px', padding: 0, height: 'auto', appearance: 'none', WebkitAppearance: 'none' },
    opt:          { backgroundColor: '#1e2330', color: '#ffffff' },
    field:        { border: '1px solid #4a5568', borderRadius: '6px', padding: '11px 14px', marginBottom: '18px', background: 'rgba(255,255,255,0.05)' },
    fieldFocus:   { border: '1px solid #2B44FF' },
    grid3:        { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '4px' },
    grid2:        { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '4px' },
    btnPrimary:   { background: '#2B44FF', color: '#fff', border: 'none', borderRadius: '6px', padding: '12px 32px', cursor: 'pointer', fontWeight: 700, fontSize: '14px', display: 'inline-flex', alignItems: 'center', gap: '6px', letterSpacing: '0.5px' },
    saveRow:      { display: 'flex', justifyContent: 'flex-end', paddingTop: '16px' },
    spinner:      { display: 'inline-block', width: '14px', height: '14px', border: '2px solid rgba(255,255,255,.3)', borderRadius: '50%', borderTopColor: '#fff', animation: 'spin 1s ease-in-out infinite' },
    card:         { backgroundColor: 'var(--secbgColor)', border: '1px solid #39404e', borderRadius: '10px', padding: '32px' },
    toast:        { position: 'fixed', bottom: '24px', right: '24px', padding: '12px 20px', borderRadius: '6px', fontSize: '14px', fontWeight: 500, zIndex: 9999, color: '#fff' },
    req:          { color: '#FD2222', marginLeft: '2px' },
    banner:       { background: 'rgba(43,68,255,0.12)', border: '1px solid rgba(43,68,255,0.4)', borderRadius: '8px', padding: '14px 18px', marginBottom: '24px', color: '#93aaff', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '10px' },
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const dialingOptions = getDialingCodeOptions();

  const countryName = (code) => countries.find(c => (c.alpha3 || c.code) === code)?.countryname || code || '—';
  const val = (v) => v?.trim() ? v.trim() : '—';

  if (!isAuthenticated) {
    return (
      <div style={S.page}>
        <Header />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 53px)' }}>
          <div style={{ ...S.card, textAlign: 'center' }}>
            <h1 style={{ color: '#ffffff', fontSize: 22, fontWeight: 600, marginBottom: 12 }}>Access Denied</h1>
            <p style={{ color: '#a0aec0' }}>Please sign in to view your profile.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <Header />
      <div style={S.container}>
        <h1 style={S.title}>My Profile</h1>

       

        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ ...S.sectionTitle, margin: 0 }}>Personal Information</h2>
            {!isEditing && !isLoading && userDetails.firstname && (
              <button onClick={() => setIsEditing(true)} style={{ ...S.btnPrimary, padding: '8px 18px', fontSize: '13px' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>edit</span>
                Edit
              </button>
            )}
          </div>

          {isLoading && !userDetails.firstname ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
              <div className="spinner" />
            </div>
          ) : !isEditing ? (
            /* ── Read-only view ── */
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
              {[
                { label: 'First Name',    v: val(userDetails.firstname) },
                { label: 'Last Name',     v: val(userDetails.lastname) },
                { label: 'Gender',        v: val(userDetails.gender) },
                { label: 'Email',         v: val(userDetails.email) },
                { label: 'Company',       v: val(userDetails.companyName) },
                { label: 'Ethnicity',     v: val(userDetails.ethnicity) },
                { label: 'Address',       v: val(userDetails.address) },
                { label: 'City',          v: val(userDetails.city) },
                { label: 'Postal Code',   v: val(userDetails.postalCode) },
                { label: 'Country',       v: countryName(userDetails.country) },
                { label: 'State / Region',v: val(userDetails.state) },
                { label: 'Home Phone',    v: [userDetails.homePhoneDialingCode, userDetails.homePhone].filter(Boolean).join(' ') || '—' },
                { label: 'Mobile Number', v: [userDetails.mobileDialingCode, userDetails.mobileNumber].filter(Boolean).join(' ') || '—' },
              ].map(({ label, v }) => (
                <div key={label}>
                  <div style={S.label}>{label}</div>
                  <div style={{ color: '#ffffff', fontSize: '15px', wordBreak: 'break-word' }}>{v}</div>
                </div>
              ))}
            </div>
          ) : (
            /* ── Edit form ── */
            <>
              <div style={S.grid3}>
                <div>
                  <label style={S.label}>First Name<span style={S.req}>*</span></label>
                  <div style={S.field}>
                    <input style={S.input} type="text" value={userDetails.firstname} onChange={e => handleChange('firstname', e.target.value)} placeholder="Enter first name" disabled={isLoading} />
                  </div>
                </div>
                <div>
                  <label style={S.label}>Last Name<span style={S.req}>*</span></label>
                  <div style={S.field}>
                    <input style={S.input} type="text" value={userDetails.lastname} onChange={e => handleChange('lastname', e.target.value)} placeholder="Enter last name" disabled={isLoading} />
                  </div>
                </div>
                <div>
                  <label style={S.label}>Gender<span style={S.req}>*</span></label>
                  <div style={S.field}>
                    <select style={S.select} value={userDetails.gender} onChange={e => handleChange('gender', e.target.value)} disabled={isLoading}>
                      <option style={S.opt} value="">Select Gender</option>
                      <option style={S.opt} value="male">Male</option>
                      <option style={S.opt} value="female">Female</option>
                      <option style={S.opt} value="non-binary">Non-binary</option>
                      <option style={S.opt} value="prefer-not-to-say">Prefer not to say</option>
                    </select>
                  </div>
                </div>
              </div>

              <div style={S.grid3}>
                <div>
                  <label style={S.label}>Email</label>
                  <div style={S.field}>
                    <input style={S.input} type="email" value={userDetails.email} onChange={e => handleChange('email', e.target.value)} placeholder="Enter email address" disabled={isLoading} />
                  </div>
                </div>
                <div>
                  <label style={S.label}>Company Name</label>
                  <div style={S.field}>
                    <input style={S.input} type="text" value={userDetails.companyName} onChange={e => handleChange('companyName', e.target.value)} placeholder="Enter company name" disabled={isLoading} />
                  </div>
                </div>
                <div>
                  <label style={S.label}>Ethnicity</label>
                  <div style={S.field}>
                    <input style={S.input} type="text" value={userDetails.ethnicity} onChange={e => handleChange('ethnicity', e.target.value)} placeholder="Enter ethnicity" disabled={isLoading} />
                  </div>
                </div>
              </div>

              <div style={S.grid3}>
                <div>
                  <label style={S.label}>Address</label>
                  <div style={S.field}>
                    <input style={S.input} type="text" value={userDetails.address} onChange={e => handleChange('address', e.target.value)} placeholder="Street address" disabled={isLoading} />
                  </div>
                </div>
                <div>
                  <label style={S.label}>City</label>
                  <div style={S.field}>
                    <input style={S.input} type="text" value={userDetails.city} onChange={e => handleChange('city', e.target.value)} placeholder="City" disabled={isLoading} />
                  </div>
                </div>
                <div>
                  <label style={S.label}>Postal Code<span style={S.req}>*</span></label>
                  <div style={S.field}>
                    <input style={S.input} type="text" value={userDetails.postalCode} onChange={e => handleChange('postalCode', e.target.value)} placeholder="Postal code" disabled={isLoading} />
                  </div>
                </div>
              </div>

              <div style={S.grid2}>
                <div>
                  <label style={S.label}>Country<span style={S.req}>*</span></label>
                  <div style={S.field}>
                    <select style={S.select} value={userDetails.country} onChange={e => handleChange('country', e.target.value)} disabled={isLoading}>
                      <option style={S.opt} value="">Select Country</option>
                      {countries.map((c, i) => <option key={i} style={S.opt} value={c.alpha3 || c.code}>{c.countryname}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label style={S.label}>State / Region<span style={S.req}>*</span></label>
                  <div style={S.field}>
                    <select style={S.select} value={userDetails.state} onChange={e => handleChange('state', e.target.value)} disabled={!userDetails.country || isLoading}>
                      <option style={S.opt} value="">Select State/Region</option>
                      {regions.map((r, i) => <option key={i} style={S.opt} value={r.regionname}>{r.regionname}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div style={S.grid2}>
                <div>
                  <label style={S.label}>Home Phone</label>
                  <DialingSelect
                    codeVal={userDetails.homePhoneDialingCode} codeOnChange={e => handleChange('homePhoneDialingCode', e.target.value)}
                    numVal={userDetails.homePhone} numOnChange={e => handleChange('homePhone', e.target.value)} numPlaceholder="Home phone" disabled={isLoading}
                    fieldStyle={S.field} selectStyle={S.select} optStyle={S.opt} inputStyle={S.input} options={dialingOptions} />
                </div>
                <div>
                  <label style={S.label}>Mobile Number</label>
                  <DialingSelect
                    codeVal={userDetails.mobileDialingCode} codeOnChange={e => handleChange('mobileDialingCode', e.target.value)}
                    numVal={userDetails.mobileNumber} numOnChange={e => handleChange('mobileNumber', e.target.value)} numPlaceholder="Mobile number" disabled={isLoading}
                    fieldStyle={S.field} selectStyle={S.select} optStyle={S.opt} inputStyle={S.input} options={dialingOptions} />
                </div>
              </div>

              <div style={{ ...S.saveRow, gap: '12px' }}>
                {isProfileComplete(userDetails) && (
                  <button onClick={() => setIsEditing(false)} style={{ ...S.btnPrimary, background: 'transparent', border: '1px solid #4a5568' }} disabled={isLoading}>
                    Cancel
                  </button>
                )}
                <button onClick={saveProfile} style={S.btnPrimary} disabled={isLoading}>
                  {isLoading && <span style={S.spinner} />}
                  {isLoading ? 'Saving...' : 'SAVE PROFILE'}
                </button>
              </div>
            </>
          )}
        </div>

        {subscription && (subscription.subscriptionid != 'free' && subscription.subscriptiontype) && (
          <div style={{ ...S.card, marginTop: '24px' }}>
            <h2 style={S.sectionTitle}>Subscription Details</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
              <div>
                <div style={S.label}>Plan Type</div>
                <div style={{ color: '#ffffff', fontSize: '15px', fontWeight: 600 }}>{subscription.subscriptiontype || '—'}</div>
              </div>
              <div>
                <div style={S.label}>Start Date</div>
                <div style={{ color: '#ffffff', fontSize: '15px' }}>{fmtDate(subscription.subscriptionstart)}</div>
              </div>
              <div>
                <div style={S.label}>End Date</div>
                <div style={{ color: '#ffffff', fontSize: '15px' }}>{fmtDate(subscription.subscriptionend)}</div>
              </div>
              <div>
                <div style={S.label}>Subscription ID</div>
                <div style={{ color: '#ffffff', fontSize: '13px' }}>{subscription.subscriptionid || '—'}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {toast.show && (
        <div style={{ ...S.toast, background: toast.type === 'success' ? '#1a7f4b' : '#c0392b' }}>
          {toast.message}
        </div>
      )}
    </div>
  );
}
